import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { restoreSessionWithToken, signInWithPassword } from '../api/auth';

const AUTH_STORAGE_KEY = 'care_auth';

function readAuthFromStorage() {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!stored) {
      return { token: null, user: null };
    }
    const parsed = JSON.parse(stored);
    return {
      token: parsed?.token || null,
      user: parsed?.user || null
    };
  } catch (error) {
    return { token: null, user: null };
  }
}

function writeAuthToStorage(authState) {
  if (authState.token) {
    localStorage.setItem(
      AUTH_STORAGE_KEY,
      JSON.stringify({
        token: authState.token,
        user: authState.user
      })
    );
    return;
  }

  localStorage.removeItem(AUTH_STORAGE_KEY);
}

const initialAuth = readAuthFromStorage();

const initialState = {
  token: initialAuth.token,
  user: initialAuth.user,
  status: 'idle',
  isAuthChecked: false,
  error: null
};

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ email, password }) => signInWithPassword({ identity: email, password })
);

export const restoreSession = createAsyncThunk(
  'auth/restoreSession',
  async (_, { getState, rejectWithValue }) => {
    const { token, user } = getState().auth;
    if (!token) {
      return { token: null, user: null };
    }

    try {
      return await restoreSessionWithToken({ token, user });
    } catch (error) {
      return rejectWithValue(error.message || 'Session restore failed');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout(state) {
      state.token = null;
      state.user = null;
      state.status = 'idle';
      state.isAuthChecked = true;
      state.error = null;
      writeAuthToStorage(state);
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(restoreSession.pending, (state) => {
        state.status = 'checking';
        state.error = null;
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.isAuthChecked = true;
        state.token = action.payload?.token || null;
        state.user = action.payload?.user || null;
        state.status = state.token ? 'succeeded' : 'idle';
        writeAuthToStorage(state);
      })
      .addCase(restoreSession.rejected, (state, action) => {
        state.isAuthChecked = true;
        state.status = 'idle';
        state.token = null;
        state.user = null;
        state.error = action.payload || action.error.message || 'Session restore failed';
        writeAuthToStorage(state);
      })
      .addCase(loginUser.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isAuthChecked = true;
        state.status = 'succeeded';
        state.token = action.payload.token;
        state.user = action.payload.user;
        writeAuthToStorage(state);
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isAuthChecked = true;
        state.status = 'failed';
        state.error = action.error.message || 'Login failed';
      });
  }
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
